'use client';

import { classNames } from '@commenda/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export type SelectWithSearchComponentProps = {
  inputValue: string;
  setInputValue: (arg: string) => unknown;
  options: {
    value: string;
    displayedValue: string;
    badge?: React.ReactNode;
    // Optional React node for additional content (price, year, etc.)
    additionalContent?: React.ReactNode;
  }[];
  maxHeight?: number;
  error?: string;
  placeholder?: string;
  onChangeInputValue?: (arg: string) => void;
  disabled?: boolean;
  displayValueIfDisabled?: string;
  // New prop to control whether custom values are allowed
  allowCustomValue?: boolean;
  label?: string;
  icon?: React.ReactNode;
};

const SelectWithSearch = ({
  inputValue,
  options,
  setInputValue,
  maxHeight,
  error,
  placeholder,
  onChangeInputValue,
  disabled,
  displayValueIfDisabled,
  allowCustomValue = false, // Default to false (select-only mode)
  label,
  icon,
}: SelectWithSearchComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const [displayValue, setDisplayValue] = useState(() => {
    const initialOption = options.find((item) => item.value === inputValue);
    return initialOption?.displayedValue || '';
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Memoize filtered items based on search value
  const filteredItems = useMemo(() => {
    return options.filter((item) =>
      searchValue
        ? item.displayedValue.toLowerCase().includes(searchValue.toLowerCase())
        : true
    );
  }, [options, searchValue]);

  useEffect(() => {
    const selectedOption = options.find((item) => item.value === inputValue);
    if (selectedOption) {
      setDisplayValue(selectedOption.displayedValue);
    } else if (allowCustomValue) {
      setDisplayValue(inputValue);
    }
  }, [inputValue, options, allowCustomValue]);

  useEffect(() => {
    if (filteredItems?.length === 0 && inputValue && inputValue.length > 0) {
      setHighlightedIndex(0);
    }
  }, [filteredItems, inputValue]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        const container = listRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();

        if (elementRect.top < containerRect.top) {
          container.scrollTop = highlightedElement.offsetTop;
        } else if (elementRect.bottom > containerRect.bottom) {
          container.scrollTop =
            highlightedElement.offsetTop -
            containerRect.height +
            elementRect.height;
        }
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (value: string) => {
    setInputValue(value);
    const selectedOption = options.find((item) => item.value === value);
    if (selectedOption) {
      setDisplayValue(selectedOption.displayedValue);
      setSearchValue(''); // Clear search after selection
    }
    setIsOpen(false);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLLIElement>
  ) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === filteredItems.length - 1 ? -1 : prevIndex + 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === -1 ? filteredItems.length - 1 : prevIndex - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
          handleSelect(filteredItems[highlightedIndex].value);
        } else if (!allowCustomValue) {
          // In select-only mode, don't allow custom values
          event.preventDefault();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isOpen) {
      // When dropdown is open, use input for searching
      setSearchValue(value);
    } else {
      // When dropdown is closed
      if (allowCustomValue) {
        setDisplayValue(value);
        if (onChangeInputValue) {
          onChangeInputValue(value);
        }
      }
      // In select-only mode, don't allow editing when closed
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Show search value when focused, or empty to show all options
    setSearchValue('');
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setSearchValue(''); // Clear search value on blur
      // Reset display value to selected option if in select-only mode
      if (!allowCustomValue) {
        const selectedOption = options.find(
          (item) => item.value === inputValue
        );
        if (selectedOption) {
          setDisplayValue(selectedOption.displayedValue);
        } else {
          setDisplayValue('');
        }
      }
    }, 100);
  };

  return (
    <div className="relative">
      <input
        disabled={disabled}
        ref={inputRef}
        type="text"
        placeholder={placeholder || 'Select a service'}
        className={classNames(
          'flex h-11 w-full txt-medium-regular rounded-md px-3 bg-bg-primary text-text-primary txt-medium-regular placeholder:text-text-placeholder ring-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled hide-date-icon appearance-none',
          error
            ? 'border border-border-error focus-visible:border-border-error'
            : 'border border-border-primary focus-visible:border-fg-brand'
        )}
        value={
          disabled
            ? displayValueIfDisabled
            : isOpen
              ? searchValue
              : displayValue
        }
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        aria-controls="combobox-list"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        readOnly={!allowCustomValue && !isOpen} // Make input readonly in select-only mode when not searching
      />
      {isOpen && filteredItems.length > 0 && (
        <div className="border border-border-primary pb-2 absolute z-50 w-full shadow-lg bg-white rounded-md mt-2">
          {label && (
            <span className="flex items-center justify-start gap-1 border-b border-border-primary py-2 px-3 rounded-none">
              {icon && <div className="w-4 h-4 text-fg-tertiary">{icon}</div>}
              <span className="text-fg-tertiary txt-small-medium">{label}</span>
            </span>
          )}
          <ul
            ref={listRef}
            id="combobox-list"
            role="listbox"
            className={`w-full overflow-y-auto ${
              label ? 'border border-t-0 border-border-primary' : 'border-none'
            }`}
            style={maxHeight ? { maxHeight } : undefined}
          >
            {filteredItems.map((item, index) => (
              <li
                key={index}
                id={`combobox-option-${index}`}
                role="option"
                aria-selected={highlightedIndex === index}
                className={`py-2 px-3 w-full cursor-pointer transition-colors ${
                  highlightedIndex === index
                    ? 'bg-bg-secondary'
                    : 'hover:bg-bg-secondary'
                }`}
                onMouseDown={() => handleSelect(item.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex flex-col items-start justify-center gap-0.5">
                  <div className="flex items-center gap-md">
                    <span className="text-text-secondary txt-small-medium">
                      {item.displayedValue}
                    </span>
                    {item.badge && item.badge}
                  </div>

                  {item.additionalContent && (
                    <div className="flex items-center gap-2 text-text-quinary txt-extra-small-regular">
                      {item.additionalContent}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export { SelectWithSearch };
